import { useRef } from 'react';
import MaskedInput from 'react-text-mask';

import mergeRefs from '~/shared/utils/mergeRefs';

import { Input } from '../Input';
import Typography from '../Typography';

export default function MaskedDateInput({
  value,
  onInternalValueChange,
  onCalendarDateChange,
  error,
  setError,
}: {
  value: string;
  onInternalValueChange: (v: string) => void;
  onCalendarDateChange: (v: string) => void;
  error?: string;
  setError: (err?: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    const splitInputValue = inputValue.split('/');
    const [year, month, day] = splitInputValue;

    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);

    const isDateComplete = splitInputValue.every(
      (part, i) => (i === 0 ? part.length === 4 : part.length === 2) && !part.includes('_')
    );

    if (isDateComplete && month && !!yearNum && !!dayNum) {
      const maxDaysInMonth = getMaxDaysInMonth(monthNum, yearNum);
      if (maxDaysInMonth < dayNum) {
        setError(`${dayNum} is an invalid day for this month`);
      } else {
        setError(undefined);
        onInternalValueChange(inputValue);
        onCalendarDateChange(inputValue);
        return;
      }
    }

    onInternalValueChange(inputValue);
  };

  const dateMask = (value: string) => {
    const [_year, month, day] = value.split('/').map((part) => part || '');
    const monthMask = [/[0-1]/, month?.[0] === '1' ? /[0-2]/ : /[1-9]/];
    const dayMask = [/[0-3]/, day?.[0] === '3' ? /[0-1]/ : /[0-9]/];
    const yearMask = [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];

    return [...yearMask, '/', ...monthMask, '/', ...dayMask];
  };

  return (
    <>
      <MaskedInput
        mask={dateMask}
        value={value}
        onChange={handleInputChange}
        render={(ref, props) => (
          <Input
            ref={mergeRefs(ref, inputRef)}
            {...props}
            type="text"
            placeholder="No date selected"
          />
        )}
      />

      {error && (
        <Typography variant="p3" fontWeight={400} color="text-red">
          {error}
        </Typography>
      )}
    </>
  );
}

const getMaxDaysInMonth = (month: number, year: number) => {
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

  if (month === 2) {
    return isLeapYear ? 29 : 28;
  }

  if ([4, 6, 9, 11].includes(month)) {
    return 30;
  }

  return 31;
};
